import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CalendarDays, RotateCw, Printer } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const DAYS = [
  { key: "sat", label: "السبت" },
  { key: "sun", label: "الأحد" },
  { key: "mon", label: "الاثنين" },
  { key: "tue", label: "الثلاثاء" },
  { key: "wed", label: "الأربعاء" },
  { key: "thu", label: "الخميس" },
];

const CLINICS = Array.from({ length: 30 }, (_, i) => i + 1);

const cellKey = (clinic, day) => `${clinic}__${day}`;

const StandaloneWrapper = ({ onRefresh, onPrint, children }) => (
  <div className="min-h-screen bg-slate-50" dir="rtl">
    <div className="bg-white border-b shadow-sm no-print">
      <div className="max-w-full mx-auto px-4 py-4 flex items-center gap-3">
        <img src="/lucent-logo.png" alt="LUCENT" className="h-10" />
        <div className="flex-1">
          <h1 className="text-lg font-bold text-[#655dc6] flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            جدول الأطباء الأسبوعي
          </h1>
          <p className="text-xs text-slate-500">Weekly Doctors Schedule — Lucent Dental Center</p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} className="gap-1.5" data-testid="refresh-schedule">
          <RotateCw className="w-3.5 h-3.5" />
          تحديث
        </Button>
        <Button size="sm" onClick={onPrint} className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-1.5" data-testid="print-schedule">
          <Printer className="w-3.5 h-3.5" />
          طباعة
        </Button>
      </div>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const EmbeddedWrapper = ({ onPrint, children }) => (
  <div dir="rtl">
    <div className="flex justify-end mb-3 no-print">
      <Button size="sm" onClick={onPrint} className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-1.5" data-testid="print-schedule-embedded">
        <Printer className="w-3.5 h-3.5" />
        طباعة الجدول
      </Button>
    </div>
    {children}
  </div>
);

const ClinicSchedule = ({ standalone = false }) => {
  const [doctors, setDoctors] = useState([]);
  const [entries, setEntries] = useState({}); // { "clinic__day": {doctor_id, doctor_name, from_time, to_time} }
  const [loading, setLoading] = useState(true);
  const [savingCell, setSavingCell] = useState(null);

  const doctorMap = useMemo(() => {
    const m = {};
    for (const d of doctors) m[d.id] = d;
    return m;
  }, [doctors]);

  // Deduplicate doctors by name (some doctors are listed in multiple specialties)
  const uniqueDoctors = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const d of doctors) {
      const key = (d.name || "").trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(d);
    }
    return out.sort((a, b) => (a.name || "").localeCompare(b.name || "", "ar"));
  }, [doctors]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [drRes, schRes] = await Promise.all([
        axios.get(`${API}/doctors`),
        axios.get(`${API}/clinic-schedule`),
      ]);
      setDoctors(drRes.data?.doctors || []);
      const map = {};
      for (const e of (schRes.data || [])) {
        map[cellKey(e.clinic, e.day)] = e;
      }
      setEntries(map);
    } catch {
      toast.error("فشل تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const updateCell = async (clinic, day, patch) => {
    const key = cellKey(clinic, day);
    const current = entries[key] || {};
    const next = { ...current, ...patch };
    setEntries(prev => ({ ...prev, [key]: next }));
    setSavingCell(key);
    try {
      await axios.put(`${API}/clinic-schedule`, {
        clinic,
        day,
        doctor_id: next.doctor_id || "",
        doctor_name: next.doctor_name || "",
        from_time: next.from_time || "",
        to_time: next.to_time || "",
      });
    } catch {
      toast.error("فشل الحفظ");
      fetchAll(); // refresh
    } finally {
      setSavingCell(null);
    }
  };

  const clearCell = async (clinic, day) => {
    const key = cellKey(clinic, day);
    setEntries(prev => {
      const c = { ...prev };
      delete c[key];
      return c;
    });
    try {
      await axios.delete(`${API}/clinic-schedule/${clinic}/${day}`);
    } catch {
      toast.error("فشل الحذف");
      fetchAll();
    }
  };

  const handlePrint = () => window.print();

  const onDoctorChange = (clinic, day, value) => {
    if (value === "__CLEAR__") {
      clearCell(clinic, day);
      return;
    }
    const d = doctorMap[value];
    updateCell(clinic, day, { doctor_id: value, doctor_name: d?.name || "" });
  };

  const Wrapper = standalone
    ? ({ children }) => <StandaloneWrapper onRefresh={fetchAll} onPrint={handlePrint}>{children}</StandaloneWrapper>
    : ({ children }) => <EmbeddedWrapper onPrint={handlePrint}>{children}</EmbeddedWrapper>;

  if (loading) {
    return (
      <Wrapper>
        <div className="flex items-center justify-center h-64">
          <div className="lucent-loader"><img src="/lucent-logo.png" alt="LUCENT" /><div className="lucent-loader-bar"></div></div>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {/* PRINT-ONLY view: static readable table — no header, fits one A4 page */}
      <div className="print-only" dir="rtl">
        <table className="print-table">
          <thead>
            <tr>
              <th>العيادة</th>
              {DAYS.map(d => <th key={d.key}>{d.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {CLINICS.map(c => (
              <tr key={c}>
                <td className="clinic-col">{c}</td>
                {DAYS.map(d => {
                  const e = entries[cellKey(c, d.key)] || {};
                  return (
                    <td key={d.key}>
                      {e.doctor_name && <div className="doc-name">{e.doctor_name}</div>}
                      {(e.from_time || e.to_time) && (
                        <div className="time-range" dir="ltr">
                          {e.from_time || "—"} — {e.to_time || "—"}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* SCREEN view: interactive table */}
      <Card className="border-0 shadow-sm no-print">
        <CardContent className="pt-6">
          <div className="overflow-auto">
            <table className="w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr>
                  <th className="sticky right-0 z-10 bg-[#655dc6] text-white p-2 border border-[#5449a8] rounded-tr-lg w-24">
                    العيادة
                  </th>
                  {DAYS.map((d, i) => (
                    <th
                      key={d.key}
                      className={`bg-[#655dc6] text-white p-2 border border-[#5449a8] min-w-[220px] ${i === DAYS.length - 1 ? "rounded-tl-lg" : ""}`}
                    >
                      {d.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CLINICS.map((c) => (
                  <tr key={c}>
                    <td className="sticky right-0 z-10 bg-[#FAF9FE] text-[#655dc6] font-bold text-center p-2 border border-slate-200">
                      {c}
                    </td>
                    {DAYS.map((d) => {
                      const key = cellKey(c, d.key);
                      const entry = entries[key] || {};
                      const isSaving = savingCell === key;
                      return (
                        <td
                          key={d.key}
                          className={`bg-white border border-slate-200 p-1.5 align-top ${isSaving ? "opacity-60" : ""}`}
                          data-testid={`cell-${c}-${d.key}`}
                        >
                          <div className="space-y-1">
                            <Select
                              value={entry.doctor_id || ""}
                              onValueChange={(v) => onDoctorChange(c, d.key, v)}
                            >
                              <SelectTrigger className="h-8 text-xs bg-slate-50">
                                <SelectValue placeholder="اختر طبيب" />
                              </SelectTrigger>
                              <SelectContent>
                                {entry.doctor_id && (
                                  <SelectItem value="__CLEAR__" className="text-red-500">
                                    — مسح —
                                  </SelectItem>
                                )}
                                {uniqueDoctors.map(dr => (
                                  <SelectItem key={dr.id} value={dr.id}>
                                    {dr.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <div className="flex gap-1">
                              <Input
                                type="time"
                                value={entry.from_time || ""}
                                onChange={(e) => updateCell(c, d.key, { from_time: e.target.value })}
                                className="h-8 text-xs px-1"
                                dir="ltr"
                                title="من"
                              />
                              <Input
                                type="time"
                                value={entry.to_time || ""}
                                onChange={(e) => updateCell(c, d.key, { to_time: e.target.value })}
                                className="h-8 text-xs px-1"
                                dir="ltr"
                                title="إلى"
                              />
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400 mt-3">
            التغييرات تُحفظ تلقائياً • 30 عيادة × 6 أيام (السبت – الخميس)
          </p>
        </CardContent>
      </Card>
    </Wrapper>
  );
};

export default ClinicSchedule;
